<template>
  <v-menu
    :key="`${collapsable && miniVariant}_account_menu`"
    v-model="switcherActive"
    offset-y
    max-height="66%"
  >
    <template #activator="{on}">
      <!-- Avatar Section Compact -->
      <div
        v-if="collapsable && miniVariant"
        v-ripple="accounts.length > 0"
        style="max-height: 64px"
        class="text-center py-3"
        v-on="accounts.length>0 ? on : {}"
      >
        <account-avatar
          :account-name="accountName"
          :color="accountColor"
        />
      </div>
      <!-- Avatar Section Full  -->
      <v-list-item
        v-else
        class="pl-3"
        style="height: 56px"
        v-on="accounts.length>0 ? on : {}"
      >
        <v-list-item-avatar
          v-if="!miniVariant"
          class="d-inline pr-0 mr-3 my-0 py-0"
          size="32"
        >
          <account-avatar
            :account-name="accountName"
            :color="accountColor"
          />
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title>{{ accountName }}</v-list-item-title>
          <v-list-item-subtitle>{{ userName }}</v-list-item-subtitle>
        </v-list-item-content>
        <v-list-item-action v-if="accounts.length>0">
          <v-icon>mdi-menu-down</v-icon>
        </v-list-item-action>
      </v-list-item>
      <!-- /// end avatar -->
    </template>
    <!-- Account Switcher Items -->
    <v-list class="py-0 my-0">
      <v-list-item-group
        :value="currentAccountId"
        class="d-menu py-0"
      >
        <template v-for="(a, i) in accounts">
          <v-list-item
            :id="`account_switcher_li_${a.id}`"
            :key="`account_switcher_li_${i}`"
            :value="a.id"
            :disabled="a.id === currentAccountId"
            class="pl-2"
            @click="changeAccount(a)"
          >
            <v-list-item-avatar class="pr-0 mr-2">
              <account-avatar
                :account-name="a.accountName"
                :color="a.color"
              />
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>{{ a.accountName }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-divider
            v-if="i !== accounts.length-1"
            :key="`account_switcher_divider_${i}`"
          />
        </template>
      </v-list-item-group>
    </v-list>
  </v-menu>
</template>

<script lang="ts" setup>
import AccountAvatar from '@/components/views/main/AccountAvatar.vue';

import { computed, ref, watch } from 'vue';
import i18n from '@/plugins/i18n';
import { performAction } from '@/services/vue-utils';
import store from '@/store/store';
import { startAutoUpdates, stopAutoUpdates } from '@/services/autoupdater';
import { startTodayUpdatesAsync } from '@/services/now-updater';
import UserAccount from '@/model/UserAccount';
import { useRouter } from 'vue-router/composables';
import scrollIntoView from 'scroll-into-view-if-needed';

const router = useRouter();
const props = defineProps({
  miniVariant: { type: Boolean, required: false, default: false },
  collapsable: { type: Boolean, required: false, default: false },
});

const switcherActive = ref(false);

const newAccountName = ref(null as string | null);
const accountName = computed(() => store.state.settings.account.accountName ?? newAccountName.value ?? '');

const newUserName = ref(null as string | null);
const userName = computed(() => store.state.settings.account.username || newUserName.value || '');

const accounts = computed(() => store.getters.userAccounts);

const accountColor = computed(() => store.state.settings.account.color ?? 'brand');

const currentAccountId = computed(() => store.state.settings.account.id);

watch(switcherActive, () => {
  if (!switcherActive.value) { return; }
  setTimeout(() => {
    const elm = document.getElementById(`account_switcher_li_${currentAccountId.value}`);
    if (elm === null) { return; }

    scrollIntoView(elm, {
      behavior: 'auto',
      scrollMode: 'if-needed',
      block: 'center',
    });
  }, 100);
});

const changeAccount = async (a: UserAccount) => {
  newAccountName.value = a.accountName;
  newUserName.value = store.state.settings.account.username ?? '';

  await performAction(
    null,
    i18n.tc('error.load_data'),
    async () => {
      await stopAutoUpdates();
      store.dispatch('switchAccount', { id: a.id, token: a.token, userName: newUserName.value });
      // an entity route (numeric :id, e.g. /newsletter/123) belongs to the
      // previous account - reload on its parent list instead; other routes
      // reload in place so the user can inspect the same page across accounts;
      // location-based like router.go(0) was, so unsaved-changes protection
      // stays the native beforeunload prompt and no route-leave guard can stall
      const route = router.currentRoute;
      if (Number(route.params.id) > 0) {
        window.location.replace(router.resolve(route.path.replace(/\/[^/]+$/, '')).href);
      } else {
        window.location.reload();
      }
      await startTodayUpdatesAsync();
      await startAutoUpdates();
    },
  );
};

</script>
